import Link from 'next/link';
import React, { useMemo } from 'react';

import styled from '@emotion/styled';
import { useTranslations } from 'next-intl';
import { formatEther } from 'viem';
import { useAccount } from 'wagmi';

import { HubTile } from '@web/features/hub-rework/HubTile';
import { useDeposits, useRewardsOf } from '@web/features/staking/hooks';
import { useCurrentCompletedUser } from '@web/features/user/service';
import { Button, Chip } from '@web/ui/components';
import { ArrowLinkIcon, CoinIcon } from '@web/ui/icons';
import { exists } from '@web/utils/exists';

const StyledHubTile = styled(HubTile)`
  gap: ${({ theme }) => theme.spacing('2000')};
  justify-content: space-between;

  ${({ theme }) => theme.breakpoints.down('sm')} {
    gap: ${({ theme }) => theme.spacing('3500')};
  }
`;

const RewardsContent = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing('0875')};
`;

const RewardsText = styled.span`
  color: ${({ theme }) => theme.color.onOrange};
  font: ${({ theme }) => theme.typography.mono.regular};
  font-size: ${({ theme }) => theme.typescale.size['10']};
  line-height: ${({ theme }) => theme.typescale.lineHeight.condensed};
`;

const DetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing('1000')};
`;

const DetailsText = styled.span`
  color: ${({ theme }) => theme.color.onOrange};
  font: ${({ theme }) => theme.typography.body.regular};
  font-size: ${({ theme }) => theme.typescale.size['0875']};
  line-height: ${({ theme }) => theme.typescale.lineHeight.default};
  text-transform: ${({ theme }) => theme.textTransform.capitalize};
`;

const SButton = styled(Button)`
  background: ${({ theme }) => theme.color.green};
`;

export const StakingHubTile = () => {
  const t = useTranslations();

  const completedUser = useCurrentCompletedUser();
  const wallet = useAccount();
  const isWalletConnected = wallet.status === 'connected';
  const depositsQuery = useDeposits();
  const { data: rewardsOfData } = useRewardsOf();
  const dataLoading =
    depositsQuery.data === undefined || rewardsOfData === undefined;
  const isConnected = completedUser && isWalletConnected && !dataLoading;

  const rewards = useRewardsOf();
  const boosts = useCurrentCompletedUser()?.user.boosts ?? 0;

  const stakedTokenIds = useMemo(() => {
    if (!isConnected || !depositsQuery.data) return [];
    const [tokenIds] = depositsQuery.data;
    return tokenIds;
  }, [isConnected, depositsQuery.data]);

  const rewardsValue = useMemo(
    () =>
      rewards?.data &&
      Object.values(rewards.data).reduce(
        (acc, { base, bonus }) => acc + base + bonus,
        0n,
      ),
    [rewards],
  );

  const stakedPersonas = useMemo(() => {
    if (!completedUser) return [];

    return stakedTokenIds
      .map((tokenId) =>
        completedUser.user.personas.find((p) => p.tokenId === tokenId),
      )
      .filter(exists);
  }, [completedUser?.user.personas, stakedTokenIds]);

  return (
    <Link href="/staking">
      <StyledHubTile
        title={t('HUB_STAKING_TITLE')}
        badge={
          isConnected ? (
            <ArrowLinkIcon size="0625" />
          ) : (
            <Chip background="redFixed" color="onRedFixed">
              {t('STAKING_HUB_TILE_ACCESS_NOT_GRANTED')}
            </Chip>
          )
        }
        textColor="onOrange"
        background="orange"
        stateLayer="onOrange"
      >
        <RewardsContent>
          <CoinIcon size="3000" />
          <RewardsText>
            {Number(formatEther(rewardsValue ?? 0n)).toFixed(2)}
          </RewardsText>
        </RewardsContent>
        <DetailsContainer>
          <DetailsText>
            {t('STAKING_HUB_TILE_PERSONAS_STAKED', {
              count: stakedPersonas.length,
            })}
          </DetailsText>
          <DetailsText>
            {t('STAKING_HUB_TILE_BOOSTS_USED', { count: boosts })}
          </DetailsText>
        </DetailsContainer>
        {!isConnected && (
          <Link href="/staking">
            <SButton
              label={t('STAKING_HUB_TILE_CONNECT_STAKING_BUTTON')}
              fullWidth
            />
          </Link>
        )}
      </StyledHubTile>
    </Link>
  );
};
